import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { flushSync } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import MUIProvider from '~/contexts/MUIProvider';

import CalculateIcon from '@mui/icons-material/Calculate';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PublicIcon from '@mui/icons-material/Public';
import SearchIcon from '@mui/icons-material/Search';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  alpha,
  createFilterOptions,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import PageChatButton from '~/pages/AskRichardUnified/page/PageChatButton';
import PageChatLayout from '~/pages/AskRichardUnified/page/PageChatLayout';
import { usePageBridge } from '~/pages/AskRichardUnified/page/pageBridge';

import { useGetToursCommandCentre } from '~/hooks/apis/reporting/useGetToursCommandCentre';

import { getDayJs } from '~/services/TimeService';

import ApiError from '~/types/ApiError';

import { localMemberLabel, retainLocalSelections } from '~/utils/localFilterOptions';

import ActionDefinitions from './ActionDefinitions';
import { BookedPredictedGrid, BookedPredictedSummary } from './BookedPredicted';
import ConfidenceBadge, { StalePredictionContext } from './ConfidenceBadge';
import ConfidenceSelect from './ConfidenceSelect';
import DepartureActionChip from './DepartureActionChip';
import DepartureActionPills from './DepartureActionPills';
import PeriodCharts from './PeriodCharts';
import SectionHeading from './SectionHeading';
import TourDrillDown from './TourDrillDown';
import { normalizeConfidenceSelection } from './confidenceSelection';
import { toursLocalRows } from './localOptions';
import {
  ChartUnit,
  CommandFilters,
  PeriodPoint,
  TourRow,
  agg,
  basisValue,
  departureSeries,
  departureSeverity,
  filtered,
  flagLabels,
  moneyShort,
  needsAttention,
  pct,
  periods,
  severityLabels,
  sortTourRows,
  sortToursByUrgency,
  stalePredictionRows,
  sydneyMonths,
  tourRows,
  windowRange,
} from './logic';
import { tcc, tccVariables } from './palette';
import { REGION_CARD_MIN, REGION_GAP } from './regionLayout';
import { pageScrollOwners, resetPageScroll } from './scroll';
import { normalizeToursFilterChanges, toursPageView, validateToursFilters } from './toursPageAdapter';
import {
  type SelectedToursPeriod,
  overviewSearch,
  readDrillFilters,
  readToursAnalysis,
  toursSearch,
  writeDrillFilters,
} from './toursUrlState';
import { CommandDeparture, CommandFlag, Severity } from './types';

const getMarginColor = (marginRate: number | null) =>
  marginRate == null ? 'var(--tcc-empty-month, #e8edf3)' : marginRate < 0.1 ? '#63758a' : '#427db6';
const initialFilters: CommandFilters = {
  windowDays: 183,
  region: 'All',
  tourId: null,
  onlyAttention: false,
  basis: 'ss',
  confidenceTier: [],
};
// Tour table: Current Booked Numbers and predicted at handback carry the same three measures, side by side.
const columnLabels = [
  'Tour',
  'Region',
  'Load Factor',
  'TTV',
  'Margin',
  'Load Factor',
  'TTV',
  'Margin',
  'Confidence',
  'Total',
  'Hidden',
  'On Track',
  'Next 12 Months (Load Factor)',
  'Departure Actions',
];
const sortKeys = [
  'name',
  'region',
  'lf',
  'ttv',
  'm',
  'plf',
  'pttv',
  'pm',
  'confidenceTier',
  'n',
  'hidden',
  'onTrack',
  'none',
  'red',
] as const;
const columnWidths = ['15%', '5%', '4%', '5%', '6%', '4%', '5%', '6%', '9%', '5%', '4%', '5%', '10%', '17%'];
const columnGroup = (index: number) =>
  index >= 2 && index <= 4 ? 'booked' : index >= 5 && index <= 7 ? 'predicted' : null;
const groupTint = (group: 'booked' | 'predicted' | null) =>
  group === 'booked' ? alpha(tcc.booked, 0.05) : group === 'predicted' ? alpha(tcc.predicted, 0.12) : undefined;
type SortKey = (typeof sortKeys)[number];

function Rate({ rate }: { rate: number | null }) {
  return (
    <Typography component="span" variant="caption" display="block" sx={{ color: 'text.secondary', fontWeight: 600 }}>
      {pct(rate)}
    </Typography>
  );
}

function FilterControl({
  label,
  value,
  icon,
  children,
  closeOnOptionSelect = false,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  closeOnOptionSelect?: boolean;
}) {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('lg'));
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (!compact) {
    return <Box sx={{ minWidth: 0 }}>{children}</Box>;
  }
  return (
    <>
      <Tooltip title={`${label}: ${value}`}>
        <Button
          variant="outlined"
          aria-label={`${label}: ${value}`}
          aria-haspopup="dialog"
          aria-expanded={Boolean(anchor)}
          onClick={(event) => setAnchor(event.currentTarget)}
          sx={{ width: '100%', minWidth: 0, height: 40, px: 0.5, gap: 0.5, overflow: 'hidden' }}
        >
          {icon}
          <Box
            component="span"
            sx={{
              display: { xs: 'none', sm: 'block' },
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontSize: 12,
            }}
          >
            {value || label}
          </Box>
        </Button>
      </Tooltip>
      <Popover
        disableRestoreFocus={closeOnOptionSelect}
        onClick={(event) => {
          // Autocomplete options render in a portal but their clicks still bubble through this popover.
          if (closeOnOptionSelect && (event.target as HTMLElement).closest('[role="option"]')) {
            setAnchor(null);
          }
        }}
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, width: 320, maxWidth: 'calc(100vw - 32px)' } } }}
      >
        <Box role="dialog" aria-label={label} sx={{ '& .MuiFormControl-root': { width: '100%' } }}>
          {children}
        </Box>
      </Popover>
    </>
  );
}

function CommandCentreContent() {
  const location = useLocation();
  const drillId = location.pathname.includes('/tour/')
    ? decodeURIComponent(location.pathname.split('/tour/')[1])
    : null;
  const navigate = useNavigate();
  const [pendingTour, setPendingTour] = useState<string | null>(null);
  const sample = new URLSearchParams(location.search).get('sample') === '1';
  const query = useGetToursCommandCentre(!sample);
  const [sampleData, setSampleData] = useState<{ snapshot_date: string; result: Array<CommandDeparture> } | null>(null);
  const [sampleError, setSampleError] = useState(false);
  useEffect(() => {
    if (!sample) {
      return;
    }
    let cancelled = false;
    setSampleError(false);
    import('./sampleData.json').then(
      (module) => {
        if (!cancelled) {
          setSampleData(module.default as { snapshot_date: string; result: Array<CommandDeparture> });
        }
      },
      () => {
        if (!cancelled) {
          setSampleError(true);
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [sample]);
  const ready = sample ? !!sampleData : query.isSuccess;
  const snapshotAgeHours =
    !sample && query.data?.generated_at ? (Date.now() - new Date(query.data.generated_at).getTime()) / 3600000 : 0;
  const staleTooltip =
    snapshotAgeHours > 48
      ? `Predictions are ${Math.floor(snapshotAgeHours / 24)} days old; the daily refresh hasn't run`
      : null;
  const sourceRows = sample ? sampleData?.result || [] : query.data?.result || [];
  const data = staleTooltip ? stalePredictionRows(sourceRows) : sourceRows;
  const [attentionFlag, setAttentionFlag] = useState<'red' | 'amber' | 'empty' | Severity | null>(
    () => new URLSearchParams(location.search).get('severity') as Severity | null,
  );
  const filterAttention = (flag: 'red' | 'amber' | 'empty' | Severity | null) => {
    setAttentionFlag(flag);
    setFilters((current) => ({ ...current, onlyAttention: !!flag }));
  };
  const [filters, setFilters] = useState<CommandFilters>(() => ({
    ...initialFilters,
    tourId: new URLSearchParams(location.search).get('tour'),
    dateFrom: new URLSearchParams(location.search).get('from'),
    dateTo: new URLSearchParams(location.search).get('to'),
    region: new URLSearchParams(location.search).get('region') || 'All',
    basis: new URLSearchParams(location.search).get('basis') === 'rep' ? 'rep' : 'ss',
    onlyAttention: new URLSearchParams(location.search).get('attention') === '1',
    action: (new URLSearchParams(location.search).get('action') || 'all') as CommandFlag | 'all',
    confidenceTier: normalizeConfidenceSelection(new URLSearchParams(location.search).get('confidence')),
  }));
  useEffect(() => {
    if (sample && sampleData && !new URLSearchParams(location.search).has('from')) {
      setFilters((current) => ({
        ...current,
        dateFrom: getDayJs(sampleData.snapshot_date).subtract(30, 'day').format('YYYY-MM-DD'),
      }));
    }
  }, [sample, sampleData, location.search]);
  const pageRef = useRef<HTMLDivElement>(null);
  const tourTableRef = useRef<HTMLDivElement>(null);
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState(-1);
  const [initialAnalysis] = useState(() => readToursAnalysis(location.search));
  const [unit, setUnit] = useState<ChartUnit>(initialAnalysis.unit);
  const [period, setPeriod] = useState<SelectedToursPeriod | null>(initialAnalysis.period);
  const priorRange = useRef(initialAnalysis.priorRange);
  const selectPeriod = (point: PeriodPoint | null) => {
    if (point) {
      if (!period) {
        priorRange.current = { dateFrom: filters.dateFrom, dateTo: filters.dateTo };
      }
      setFilters((current) => ({ ...current, dateFrom: point.from, dateTo: point.to }));
    } else if (priorRange.current) {
      setFilters((current) => ({ ...current, ...priorRange.current }));
      priorRange.current = null;
    }
    setPeriod(point);
  };
  // "Today" is the date the numbers were calculated for: the sample's fixed snapshot date, or the Sydney date the
  // reporting service built the snapshot (generated_at). The engine computed days_to_handback against that date, so
  // using it here keeps the page consistent however late in the day (or from whichever timezone) it is opened.
  const snapshotDate =
    (sample ? sampleData?.snapshot_date : query.data?.generated_at?.slice(0, 10)) ||
    new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Australia/Sydney',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  const today = useMemo(() => new Date(`${snapshotDate}T00:00:00`), [snapshotDate]);
  const months = useMemo(() => sydneyMonths(new Date(`${snapshotDate}T00:00:00+10:00`)), [snapshotDate]);
  const attentionMatches = (d: CommandDeparture) =>
    !attentionFlag ||
    (['red', 'amber', 'empty'].includes(attentionFlag)
      ? basisValue(d, 'flag', filters.basis) === attentionFlag
      : departureSeverity(d, filters.basis) === attentionFlag);
  const inPeriod = (d: CommandDeparture) => !period || (d.dep >= period.from && d.dep <= period.to);
  const windowRows = drillId ? [] : filtered(data, filters, today);
  const scopedRows = windowRows.filter(inPeriod).filter(attentionMatches);
  const chartPoints = periods(
    scopedRows,
    filters.basis,
    unit === 'departure' ? 'month' : unit,
    windowRange(filters, today),
  );
  const departurePoints = departureSeries(scopedRows, filters.basis);
  const rows = windowRows.filter(inPeriod).filter(attentionMatches);
  const regions = [...new Set(data.map((d) => d.region))].sort();
  const cardRows = windowRows.filter(inPeriod).filter(attentionMatches);
  const heatmapRows = drillId ? [] : filtered(data, filters, today, false, true);
  const regionGroups = regions
    .map((region) => ({ region, rows: cardRows.filter((d) => d.region === region) }))
    .filter(({ rows: group }) => group.length)
    .sort((a, b) => b.rows.length - a.rows.length);
  // The summary and attention card follow the selected tour, like the regions and charts.
  const attentionCount = rows.filter((d) =>
    ['red', 'amber', 'empty'].includes(String(basisValue(d, 'flag', filters.basis))),
  ).length;
  // Search scopes every dashboard section; only a row opens the Departure Dashboard.
  // Action filters match tours, while each tour retains all scoped departures and all its action counts.
  const tableRows = filtered(data, { ...filters, action: 'all' }, today).filter(inPeriod);
  const tableHeatmapRows = heatmapRows;
  let tours = tourRows(tableRows, tableHeatmapRows, filters.basis, months);
  if (filters.action && filters.action !== 'all') {
    tours = tours.filter((tour) => tour.rows.some((d) => basisValue(d, 'flag', filters.basis) === filters.action));
  }
  if (filters.onlyAttention) {
    tours = tours.filter((tour) => (attentionFlag ? tour[attentionFlag] > 0 : needsAttention(tour)));
  }
  tours =
    sortKey === 'red'
      ? sortDir > 0
        ? sortToursByUrgency(tours).reverse()
        : sortToursByUrgency(tours)
      : sortKey
        ? sortTourRows(tours, sortKey as keyof TourRow, sortDir)
        : sortToursByUrgency(tours);
  const optionRows = toursLocalRows(data, filters, today, attentionFlag, period, 'tourId');
  const scopeOptions = tourRows(optionRows, optionRows, filters.basis, []).sort((a, b) => b.ttv - a.ttv);
  const optionTourIds = scopeOptions.map((tour) => tour.id);
  const selectedTour = filters.tourId
    ? tourRows(
        data.filter((d) => d.opp_id === filters.tourId),
        [],
        filters.basis,
        [],
      )[0]
    : undefined;
  const labelTour = (id: string) =>
    localMemberLabel(
      id,
      optionTourIds,
      optionsComplete,
      scopeOptions.find((tour) => tour.id === id)?.name ?? selectedTour?.name ?? id,
    );
  const optionsComplete = ready && !query.isFetching && !query.isError;
  const availableRegions = [
    ...new Set(toursLocalRows(data, filters, today, attentionFlag, period, 'region').map((d) => d.region)),
  ].sort();
  const actionOptions = [
    ...new Set(
      toursLocalRows(data, filters, today, attentionFlag, period, 'action').map((d) =>
        String(basisValue(d, 'flag', filters.basis)),
      ),
    ),
  ];
  const confidenceOptions = [
    ...new Set(
      toursLocalRows(data, filters, today, attentionFlag, period, 'confidenceTier').map(
        (d) => d.pred_confidence_tier || 'indicative',
      ),
    ),
  ];
  const set = <K extends keyof CommandFilters>(key: K, value: CommandFilters[K]) => {
    if (key === 'dateFrom' || key === 'dateTo') {
      setPeriod(null);
      priorRange.current = null;
    }
    setFilters((current) => ({ ...current, [key]: value }));
  };
  const summary = agg(rows, filters.basis);
  const pageView = {
    ...toursPageView({
      ready,
      sample,
      snapshotDate,
      filters,
      attentionFlag,
      rows,
      data,
      summary,
      regions,
      range: windowRange(filters, today),
    }),
    presentation: {
      granularity: unit,
      selectedPeriod: period ? { key: period.key, from: period.from, to: period.to } : null,
    },
    tourList: {
      count: tours.length,
      onlyAttention: filters.onlyAttention,
      tours: tours.slice(0, 100).map((tour) => ({ id: tour.id, name: tour.name, departureCount: tour.rows.length })),
      truncated: tours.length > 100,
    },
  };
  usePageBridge(
    {
      pageId: 'tours-command-centre',
      view: pageView,
      normalizeFilters: normalizeToursFilterChanges,
      edit: (changes) => {
        if (!ready) throw new Error('Page data is still loading.');
        const next = validateToursFilters(changes, pageView.filterSchema, pageView.filters);
        const { severity, ...patch } = next;
        if ('dateFrom' in patch || 'dateTo' in patch) {
          setPeriod(null);
          priorRange.current = null;
        }
        if ('severity' in next) setAttentionFlag(severity);
        setFilters((current) => ({ ...current, ...patch }));
      },
    },
    !drillId,
  );

  const currentSearch = () =>
    toursSearch(location.search, filters, attentionFlag, { unit, period, priorRange: priorRange.current });
  const viewDepartures = (id: string) => {
    sessionStorage.setItem('tcc-list-scroll', String(window.scrollY));
    sessionStorage.setItem(
      'tcc-owner-scroll',
      JSON.stringify(pageScrollOwners(pageRef.current).map((el) => el.scrollTop)),
    );
    sessionStorage.setItem('tcc-table-scroll', String(tourTableRef.current?.scrollTop || 0));
    sessionStorage.setItem('tcc-table-scroll-left', String(tourTableRef.current?.scrollLeft || 0));
    sessionStorage.setItem('tcc-list-url', `/tours/command-centre${currentSearch()}`);
    const params = new URLSearchParams(currentSearch());
    params.delete('tour');
    if (period) {
      params.set('from', period.from);
      params.set('to', period.to);
    }
    const seededRange = period ? { from: period.from, to: period.to } : windowRange(filters, today);
    const drillSearch = writeDrillFilters(`?${params}`, {
      dateFrom: seededRange.from,
      dateTo: seededRange.to,
      confidenceTier: filters.confidenceTier ?? [],
      basis: filters.basis,
    });
    window.history.scrollRestoration = 'manual';
    // Router transitions can keep the old list painted while the new route mounts. Mask it before resetting scroll.
    if (pageRef.current) pageRef.current.style.opacity = '0';
    resetPageScroll(pageRef.current);
    flushSync(() => setPendingTour(id));
    navigate(`/tours/command-centre/tour/${encodeURIComponent(id)}${drillSearch}`);
  };
  useEffect(() => {
    if (pendingTour || drillId || !ready) {
      return;
    }
    const target = currentSearch();
    if (target !== location.search) {
      navigate({ pathname: location.pathname, search: target }, { replace: true });
    }
  }, [filters, ready, drillId, attentionFlag, pendingTour, unit, period]);
  useLayoutEffect(() => {
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    if (drillId) resetPageScroll(pageRef.current);
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, [drillId]);
  useLayoutEffect(() => {
    if (!ready || drillId) {
      return;
    }
    const saved = sessionStorage.getItem('tcc-restore-scroll');
    if (saved) {
      const restore = () => {
        const offsets: number[] = JSON.parse(sessionStorage.getItem('tcc-owner-scroll') || '[]');
        pageScrollOwners(pageRef.current).forEach((el, index) => {
          el.scrollTop = offsets[index] || 0;
        });
        window.scrollTo(0, Number(saved));
        if (tourTableRef.current) {
          tourTableRef.current.scrollTop = Number(sessionStorage.getItem('tcc-table-scroll') || 0);
          tourTableRef.current.scrollLeft = Number(sessionStorage.getItem('tcc-table-scroll-left') || 0);
        }
      };
      restore();
      const frame = requestAnimationFrame(() => requestAnimationFrame(restore));
      sessionStorage.removeItem('tcc-restore-scroll');
      return () => cancelAnimationFrame(frame);
    }
  }, [ready, drillId]);
  useLayoutEffect(() => {
    if (pendingTour || drillId) {
      resetPageScroll(pageRef.current);
      if (pageRef.current) pageRef.current.style.opacity = '1';
    }
  }, [pendingTour, drillId, ready]);
  useEffect(() => {
    if (drillId && ready) setPendingTour(null);
  }, [drillId, ready]);
  if ((drillId && !ready) || (pendingTour && !drillId)) {
    return (
      <Container
        ref={pageRef}
        maxWidth={false}
        sx={{ py: 2 }}
        aria-label="Loading Departure Dashboard"
        data-departure-loading
      >
        <Typography variant="h4">Departure Dashboard</Typography>
        <Typography role="status">Loading departures…</Typography>
        <Skeleton width="35%" height={60} />
        <Skeleton width="60%" height={48} />
        <Skeleton variant="rounded" height={180} sx={{ my: 2 }} />
        <Skeleton variant="rounded" height={600} />
      </Container>
    );
  }
  if (ready && drillId) {
    const drillFilters: CommandFilters = {
      ...filters,
      ...readDrillFilters(location.search, filters),
      tourId: drillId,
      action: 'all',
    };
    const drillRows = filtered(data, drillFilters, today);
    return (
      <StalePredictionContext.Provider value={staleTooltip}>
        <TourDrillDown
          sample={sample}
          snapshotDate={snapshotDate}
          contextFilters={drillFilters}
          optionsComplete={optionsComplete}
          rows={drillRows}
          linkRows={data.filter((row) => row.opp_id === drillId)}
          tourId={drillId}
          basis={drillFilters.basis}
          range={windowRange(drillFilters, today)}
          back={() => {
            sessionStorage.setItem('tcc-restore-scroll', sessionStorage.getItem('tcc-list-scroll') || '0');
            navigate(
              sessionStorage.getItem('tcc-list-url') || `/tours/command-centre${overviewSearch(location.search)}`,
            );
          }}
        />
      </StalePredictionContext.Provider>
    );
  }
  const doSort = (key: SortKey) => {
    if (key === 'none') {
      return;
    }
    setSortDir(sortKey === key ? -sortDir : -1);
    setSortKey(key);
  };

  return (
    <StalePredictionContext.Provider value={staleTooltip}>
      <Container
        ref={pageRef}
        maxWidth={false}
        sx={(theme) => ({
          py: 1,
          pb: 3,
          '& .MuiButton-root, & .MuiToggleButton-root': { textTransform: 'none' },
          overflowAnchor: 'none',
          ...tccVariables(theme.palette.mode),
        })}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
          <Typography variant="h5">Tours Command Centre</Typography>
          <PageChatButton />
        </Stack>
        {ready && (
          <Typography variant="caption" color="text.secondary" display="block">
            Calculated as at{' '}
            {new Date(`${snapshotDate}T00:00:00`).toLocaleDateString('en-AU', {
              day: 'numeric',
              month: 'short',
              year: 'numeric',
            })}{' '}
            (Sydney)
          </Typography>
        )}
        {snapshotAgeHours > 36 && (
          <Typography variant="caption" color="warning.main" display="block">
            Showing data as at {snapshotDate}. Data may be out of date (last calculated{' '}
            {new Date(query.data.generated_at).toLocaleString('en-AU', { timeZone: 'Australia/Sydney' })})
          </Typography>
        )}
        {sample && (
          <Alert severity="info" sx={{ my: 1, py: 0 }}>
            Sample data — fictional tours and synthetic values
          </Alert>
        )}
        {(sample ? !ready && !sampleError : query.isPending) && (
          <Box py={3}>
            <CircularProgress aria-label="Loading command centre" />
          </Box>
        )}
        {sample && sampleError && <Alert severity="error">Could not load sample data. Reload to try again.</Alert>}
        {!sample && query.isError && (query.error as ApiError)?.status === 503 && (
          <Alert
            severity="warning"
            sx={{ my: 2 }}
            action={
              <Button color="inherit" onClick={() => navigate(`${location.pathname}?sample=1`)}>
                View with sample data
              </Button>
            }
          >
            Command centre data isn't available yet. The daily refresh hasn't run. Try again later or contact the
            analytics team.
          </Alert>
        )}
        {!sample && query.isError && (query.error as ApiError)?.status !== 503 && (
          <Alert severity="error" sx={{ my: 2 }}>
            Could not load command centre data ({query.error?.message || 'unknown error'}). Reload to try again.
          </Alert>
        )}
        {ready && (
          <>
            <Card variant="outlined" sx={{ p: { xs: 1, md: 2 }, my: 1, border: 0 }}>
              <Box
                data-testid="command-centre-filters"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'repeat(6, minmax(0, 1fr))',
                    lg: '1.05fr 1.05fr minmax(200px, 1.3fr) 1.1fr 2.2fr 1.25fr 0.85fr',
                  },
                  gap: { xs: 0.5, sm: 1, lg: 1.5 },
                  alignItems: 'center',
                  '& .MuiFormControl-root': { width: '100%', minWidth: 0 },
                  '& .MuiInputBase-root': { fontSize: { md: 12, lg: 14 } },
                  '& .MuiInputLabel-root': { fontSize: { md: 12, lg: 14 } },
                  '& .MuiInputBase-input': { minWidth: 0, textOverflow: 'ellipsis' },
                }}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <FilterControl
                    label="Departures From"
                    value={getDayJs(filters.dateFrom || snapshotDate).format('DD/MM/YYYY')}
                    icon={<CalendarMonthIcon fontSize="small" />}
                  >
                    <DatePicker
                      label="Departures From"
                      value={getDayJs(filters.dateFrom || snapshotDate)}
                      onChange={(value) => set('dateFrom', value?.isValid() ? value.format('YYYY-MM-DD') : null)}
                      format="DD/MM/YYYY"
                      slotProps={{ textField: { size: 'small', fullWidth: true, sx: { '& input': { px: 1 } } } }}
                    />
                  </FilterControl>
                  <FilterControl
                    label="Departures To"
                    value={getDayJs(filters.dateTo || getDayJs(snapshotDate).add(filters.windowDays, 'day')).format(
                      'DD/MM/YYYY',
                    )}
                    icon={<CalendarMonthIcon fontSize="small" />}
                  >
                    <DatePicker
                      label="Departures To"
                      minDate={getDayJs(filters.dateFrom || snapshotDate)}
                      value={getDayJs(
                        filters.dateTo || getDayJs(snapshotDate).add(filters.windowDays, 'day').format('YYYY-MM-DD'),
                      )}
                      onChange={(value) => set('dateTo', value?.isValid() ? value.format('YYYY-MM-DD') : null)}
                      format="DD/MM/YYYY"
                      slotProps={{ textField: { size: 'small', fullWidth: true, sx: { '& input': { px: 1 } } } }}
                    />
                  </FilterControl>
                </LocalizationProvider>
                <Box sx={{ gridColumn: { xs: '1 / -1', lg: 'auto' }, order: { xs: 1, lg: 0 }, minWidth: 0 }}>
                  <ConfidenceSelect
                    value={filters.confidenceTier ?? []}
                    onChange={(value) => set('confidenceTier', value)}
                    available={confidenceOptions}
                    complete={optionsComplete}
                    showDescriptions
                    badge
                  />
                </Box>
                <FilterControl
                  label="Departure Action"
                  value={filters.action || 'all'}
                  icon={<InfoOutlinedIcon fontSize="small" />}
                >
                  <FormControl fullWidth size="small">
                    <InputLabel>Departure Action</InputLabel>
                    <Select
                      label="Departure Action"
                      inputProps={{ 'aria-label': 'Departure Action' }}
                      value={filters.action || 'all'}
                      onChange={(event) => set('action', event.target.value as CommandFlag | 'all')}
                    >
                      <MenuItem value="all">All</MenuItem>
                      {retainLocalSelections(
                        actionOptions,
                        filters.action && filters.action !== 'all' ? [filters.action] : [],
                      ).map((value) => (
                        <MenuItem key={value} value={value}>
                          {localMemberLabel(value, actionOptions, optionsComplete, flagLabels[value])}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </FilterControl>
                <FilterControl label="Region" value={filters.region} icon={<PublicIcon fontSize="small" />}>
                  <FormControl size="small">
                    <InputLabel>Region</InputLabel>
                    <Select
                      label="Region"
                      inputProps={{ 'aria-label': 'Region' }}
                      sx={{
                        '& .MuiSelect-select': { fontSize: { lg: 12 }, pl: { lg: 1 }, pr: { lg: '28px !important' } },
                      }}
                      value={filters.region}
                      onChange={(e) => set('region', e.target.value)}
                    >
                      <MenuItem value="All">All</MenuItem>
                      {retainLocalSelections(availableRegions, filters.region !== 'All' ? [filters.region] : []).map(
                        (r) => (
                          <MenuItem key={r} value={r}>
                            {localMemberLabel(r, availableRegions, optionsComplete)}
                          </MenuItem>
                        ),
                      )}
                    </Select>
                  </FormControl>
                </FilterControl>
                <FilterControl label="Search Tour" closeOnOptionSelect value="" icon={<SearchIcon fontSize="small" />}>
                  <Autocomplete
                    fullWidth
                    size="small"
                    options={retainLocalSelections(optionTourIds, filters.tourId ? [filters.tourId] : [])}
                    value={filters.tourId}
                    getOptionLabel={labelTour}
                    isOptionEqualToValue={(option, value) => option === value}
                    filterOptions={createFilterOptions<string>({
                      stringify: (id) =>
                        `${labelTour(id)} ${scopeOptions.find((tour) => tour.id === id)?.country || ''}`,
                    })}
                    noOptionsText="No Matching Tours"
                    onChange={(_, tour) => {
                      set('tourId', tour || null);
                    }}
                    renderOption={(props, id) => (
                      <Box component="li" {...props} key={id}>
                        <Box>
                          <Typography variant="body2">{labelTour(id)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {scopeOptions.find((tour) => tour.id === id)?.country ||
                              selectedTour?.country ||
                              'Country not set'}{' '}
                            · Departures: {scopeOptions.find((tour) => tour.id === id)?.deps ?? 0}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search Tour"
                        placeholder="e.g. Vietnam"
                        inputProps={{ ...params.inputProps, 'aria-label': 'Search Tour' }}
                      />
                    )}
                  />
                </FilterControl>
                <FilterControl
                  label="Cost Basis"
                  value={filters.basis === 'ss' ? 'Sliding Scale' : 'Reporting'}
                  icon={<CalculateIcon fontSize="small" />}
                >
                  <FormControl size="small">
                    <InputLabel>Cost Basis</InputLabel>
                    <Select
                      label="Cost Basis"
                      inputProps={{ 'aria-label': 'Cost Basis' }}
                      sx={{
                        '& .MuiSelect-select': { fontSize: { lg: 12 }, pl: { lg: 1 }, pr: { lg: '28px !important' } },
                      }}
                      value={filters.basis}
                      renderValue={(value) =>
                        value === 'ss' ? 'Supplier Contract Cost (Sliding Scale)' : 'Finance Reported Cost (WBR)'
                      }
                      onChange={(e) => set('basis', e.target.value)}
                    >
                      <MenuItem value="ss">Supplier Contract Cost (Sliding Scale)</MenuItem>
                      <MenuItem value="rep">Finance Reported Cost (WBR)</MenuItem>
                    </Select>
                  </FormControl>
                </FilterControl>
              </Box>
            </Card>

            {period && (
              <Alert
                severity="info"
                sx={{ mb: 1.5 }}
                action={
                  <Button color="inherit" size="small" onClick={() => selectPeriod(null)}>
                    Show All Dates
                  </Button>
                }
              >
                Showing departures {period.from === period.to ? 'on' : 'from'}{' '}
                {new Date(`${period.from}T00:00:00`).toLocaleDateString('en-AU', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
                {period.from !== period.to &&
                  ` to ${new Date(`${period.to}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}`}
              </Alert>
            )}
            <Card variant="outlined" sx={{ p: 2, mt: 2, border: 0 }}>
              <SectionHeading
                title="Overall: All Tours"
                subtitle="Summary, departure attention and monthly performance."
              />
              <Box
                data-testid="command-centre-summary"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
                  gap: 1.5,
                }}
              >
                <BookedPredictedSummary a={summary} />
              </Box>

              <Card
                variant="outlined"
                data-testid="attention-bar"
                sx={{
                  mt: 2,
                  p: { xs: 2, md: 3 },
                  width: '100%',
                  bgcolor: alpha(tcc.amber, 0.08),
                  borderColor: alpha(tcc.amber, 0.3),
                  '& .MuiChip-root': {
                    fontSize: 16,
                    fontWeight: 700,
                    minHeight: 44,
                    height: 'auto',
                    '& .MuiChip-label': { px: 2, py: 1, whiteSpace: 'normal' },
                  },
                }}
              >
                <Typography component="h3" sx={{ fontSize: 22, fontWeight: 700 }}>
                  Departures Needing Attention ({attentionCount})
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
                  Departures needing action, on watch, or with no bookings within 120 days of handback.
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
                  {(['red', 'amber', 'empty'] as const).map((flag) => (
                    <DepartureActionChip
                      key={flag}
                      label={`Departures: ${flagLabels[flag]} (${summary[flag]})`}
                      onClick={() => filterAttention(attentionFlag === flag ? null : flag)}
                      flag={flag}
                      selected={attentionFlag === flag}
                    />
                  ))}
                </Stack>
              </Card>

              <Box>
                <PeriodCharts
                  showTourPicker={false}
                  points={chartPoints}
                  departurePoints={departurePoints}
                  basis={filters.basis}
                  tour={null}
                  tours={scopeOptions}
                  onTourChange={(id) => {
                    if (id) {
                      viewDepartures(id);
                    }
                  }}
                  unit={unit}
                  onUnitChange={(value) => {
                    setUnit(value);
                    selectPeriod(null);
                  }}
                  selected={period?.key ?? null}
                  onSelect={selectPeriod}
                  onDepartureSelect={() => undefined}
                />
              </Box>
            </Card>
            {filters.onlyAttention && (
              <Chip
                sx={{ mt: 2 }}
                label={
                  attentionFlag
                    ? `Showing: Departures: ${severityLabels[attentionFlag as Severity] || flagLabels[attentionFlag as CommandFlag]}`
                    : 'Showing: Departures Needing Attention'
                }
                onDelete={() => {
                  setAttentionFlag(null);
                  set('onlyAttention', false);
                }}
              />
            )}
            <Card variant="outlined" sx={{ p: 2, mt: 2, border: 0 }}>
              <SectionHeading
                title="Region Level"
                subtitle="Departure counts and performance by region. Click a card to filter."
                accent={tcc.predicted}
              />
              <Box
                data-testid="command-centre-region-grid"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${REGION_CARD_MIN}px), 1fr))`,
                  gap: `${REGION_GAP}px`,
                }}
              >
                {regionGroups.map(({ region, rows: group }) => {
                  const a = agg(group, filters.basis);
                  const accent = a.red ? tcc.red : a.amber ? tcc.amber : null;
                  return (
                    <Card
                      key={region}
                      variant="outlined"
                      onClick={() => set('region', filters.region === region ? 'All' : region)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          set('region', filters.region === region ? 'All' : region);
                        }
                      }}
                      role="button"
                      tabIndex={0}
                      aria-pressed={filters.region === region}
                      sx={{
                        p: 2,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        borderColor: filters.region === region ? 'primary.main' : undefined,
                        borderWidth: filters.region === region ? 2 : undefined,
                        // Worst flag in the region as a coloured edge, so problem regions stand out at a glance.
                        borderLeft: accent ? `4px solid ${accent}` : undefined,
                        transition: 'box-shadow 120ms',
                        '&:hover': { boxShadow: 2 },
                        '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight={700} lineHeight={1.3}>
                        {region}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, mb: 1 }}>
                        Departures: {a.n.toLocaleString('en-AU')}
                      </Typography>
                      <BookedPredictedGrid a={a} />
                      <Box
                        data-testid="command-centre-region-footer"
                        sx={{
                          width: '100%',
                          mt: 1.5,
                          pt: 1,
                          borderTop: '1px solid',
                          borderColor: 'divider',
                          display: 'grid',
                          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                          gridTemplateRows: 'auto auto',
                          gap: 1,
                          textAlign: 'center',
                        }}
                      >
                        {[
                          {
                            label: 'Departures: Needs Action / Watch',
                            value: (
                              <>
                                <Box component="span" sx={{ color: a.red ? tcc.redText : undefined }}>
                                  ● {a.red}
                                </Box>{' '}
                                /{' '}
                                <Box component="span" sx={{ color: a.amber ? tcc.amberText : undefined }}>
                                  ● {a.amber}
                                </Box>
                              </>
                            ),
                          },
                          {
                            label: 'Departures: No Bookings',
                            value: (
                              <Box component="span" sx={{ color: a.empty ? tcc.empty : undefined }}>
                                ○ {a.empty}
                              </Box>
                            ),
                          },
                          { label: 'Departures: Closed', value: `${a.closed} of ${a.n}` },
                          { label: 'Departures: Sold out', value: `${a.soldOut} of ${a.n}` },
                        ].map((metric, index) => (
                          <React.Fragment key={metric.label}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                gridColumn: index + 1,
                                gridRow: 1,
                                alignSelf: 'end',
                                minWidth: 0,
                                fontSize: 'clamp(0.62rem, 0.72vw, 0.72rem)',
                                lineHeight: 1.2,
                              }}
                            >
                              {metric.label}
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight={700}
                              sx={{
                                gridColumn: index + 1,
                                gridRow: 2,
                                alignSelf: 'start',
                                whiteSpace: 'nowrap',
                                fontVariantNumeric: 'tabular-nums',
                              }}
                            >
                              {metric.value}
                            </Typography>
                          </React.Fragment>
                        ))}
                      </Box>
                    </Card>
                  );
                })}
              </Box>
            </Card>
            <Card variant="outlined" sx={{ p: 2, mt: 2, border: 0 }}>
              {/* The clear pill sits beside the heading in a fixed-height row, so the table never moves when it appears. */}
              <Stack
                data-testid="command-centre-tours-heading"
                direction="row"
                alignItems="center"
                gap={2}
                sx={{ mt: 3, mb: 1, minHeight: 40, minWidth: 0 }}
              >
                <SectionHeading
                  title="Tour Level"
                  subtitle="Each action pill counts departures for that tour. Click a row to open its Departure Dashboard."
                />
              </Stack>
              <ActionDefinitions />
              {tours.length === 0 && (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography>No tours match these filters</Typography>
                  <Button
                    onClick={() => {
                      setAttentionFlag(null);
                      setPeriod(null);
                      priorRange.current = null;
                      setFilters(initialFilters);
                    }}
                  >
                    Reset Filters
                  </Button>
                </Box>
              )}
              <TableContainer
                ref={tourTableRef}
                data-testid="command-centre-tour-table"
                component={Card}
                variant="outlined"
                sx={{
                  maxHeight: 'min(68vh, 680px)',
                  overflowX: 'hidden',
                  overflowY: 'auto',
                  '@media (max-width: 1199px)': {
                    '& > table, & > table > tbody': { display: 'block' },
                    '& > table > thead': { display: 'none' },
                    '& .command-tour-row': {
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      py: 1,
                    },
                    '& .command-tour-row > .MuiTableCell-root': {
                      display: 'block',
                      minWidth: 0,
                      width: '100%',
                      borderBottom: 0,
                      px: 1.5,
                      py: 0.75,
                      textAlign: 'left',
                      overflowWrap: 'normal',
                      wordBreak: 'normal',
                      '&::before': {
                        content: 'attr(data-label)',
                        display: 'block',
                        color: 'text.secondary',
                        fontSize: '0.72rem',
                        fontWeight: 600,
                        mb: 0.25,
                      },
                    },
                    '& .command-tour-row > td:nth-of-type(2)': { gridColumn: '1 / -1', gridRow: 2 },
                    '& .command-tour-row > td:nth-of-type(3)': { gridColumn: 1, gridRow: 3 },
                    '& .command-tour-row > td:nth-of-type(6)': { gridColumn: 2, gridRow: 3 },
                    '& .command-tour-row > td:nth-of-type(4)': { gridColumn: 1, gridRow: 4 },
                    '& .command-tour-row > td:nth-of-type(7)': { gridColumn: 2, gridRow: 4 },
                    '& .command-tour-row > td:nth-of-type(5)': { gridColumn: 1, gridRow: 5 },
                    '& .command-tour-row > td:nth-of-type(8)': { gridColumn: 2, gridRow: 5 },
                    '& .command-tour-row > .MuiTableCell-root:first-of-type, & .command-tour-row > .MuiTableCell-root:nth-of-type(13), & .command-tour-row > .MuiTableCell-root:nth-of-type(14)':
                      {
                        gridColumn: '1 / -1',
                      },
                  },
                }}
              >
                <Box
                  sx={{
                    display: { xs: 'flex', lg: 'none' },
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 1.5,
                    py: 1,
                    bgcolor: 'background.paper',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="subtitle2">Tours</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Select a Tour to View Departures
                  </Typography>
                </Box>
                <Table
                  stickyHeader
                  size="small"
                  sx={{ width: '100%', maxWidth: '100%', tableLayout: { xs: 'auto', lg: 'fixed' } }}
                >
                  {/* Fixed layout takes column widths from the first row, which is the group band, so set them here. */}
                  <colgroup>
                    {columnWidths.map((width, index) => (
                      <col key={index} style={{ width }} />
                    ))}
                  </colgroup>
                  <TableHead
                    sx={{
                      '& .MuiTableCell-head': { fontWeight: 600, px: 0.5, fontSize: '0.75rem' },
                      // Two sticky header rows: the group band sits on top of the column labels.
                      '& tr:first-of-type .MuiTableCell-head': { top: 0, height: 28, py: 0.25 },
                      '& tr:nth-of-type(2) .MuiTableCell-head': { top: 29 },
                    }}
                  >
                    <TableRow>
                      <TableCell colSpan={2} />
                      <TableCell
                        colSpan={3}
                        align="center"
                        sx={{
                          bgcolor: tcc.booked,
                          color: '#fff',
                          fontSize: '0.72rem',
                          letterSpacing: 0.3,
                          textTransform: 'none',
                        }}
                      >
                        Current Booked Numbers
                      </TableCell>
                      <TableCell
                        colSpan={3}
                        align="center"
                        sx={{
                          bgcolor: tcc.predictedBand,
                          color: '#fff',
                          fontSize: '0.72rem',
                          letterSpacing: 0.3,
                          textTransform: 'none',
                        }}
                      >
                        Predicted at Handback
                      </TableCell>
                      <TableCell />
                      <TableCell
                        colSpan={3}
                        align="center"
                        sx={{
                          bgcolor: tcc.neutralBand,
                          color: '#fff',
                          fontSize: '0.72rem',
                          letterSpacing: 0.3,
                          textTransform: 'none',
                        }}
                      >
                        Departures
                      </TableCell>
                      <TableCell colSpan={2} />
                    </TableRow>
                    <TableRow>
                      {columnLabels.map((label, index) => (
                        <TableCell
                          key={`${label}-${index}`}
                          onClick={() => doSort(sortKeys[index])}
                          tabIndex={sortKeys[index] === 'none' ? undefined : 0}
                          role={sortKeys[index] === 'none' ? undefined : 'button'}
                          aria-label={
                            columnGroup(index)
                              ? `${label} ${columnGroup(index) === 'booked' ? 'Current Booked Numbers' : 'Predicted at Handback'}`
                              : undefined
                          }
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' && sortKeys[index] !== 'none') {
                              doSort(sortKeys[index]);
                            }
                          }}
                          sx={{
                            cursor: sortKeys[index] === 'none' ? 'default' : 'pointer',
                            width: columnWidths[index],
                            whiteSpace: 'normal',
                            overflowWrap: 'normal',
                            wordBreak: 'normal',
                            textAlign: index >= 2 && index <= 8 ? 'center' : 'left',
                            verticalAlign: 'middle',
                            // Booked / predicted column labels carry their group colour, continuing the band above.
                            ...(columnGroup(index) === 'booked' && { bgcolor: tcc.booked, color: '#fff' }),
                            ...(columnGroup(index) === 'predicted' && { bgcolor: tcc.predictedBand, color: '#fff' }),
                            '&:focus-visible': {
                              outline: '2px solid',
                              outlineColor: 'primary.main',
                              outlineOffset: -2,
                            },
                          }}
                        >
                          {label}
                          {index === 12 && (
                            <>
                              <Box
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
                                  gap: '2px',
                                  mt: 1,
                                }}
                              >
                                {months.map((month) => (
                                  <Typography
                                    key={month}
                                    component="span"
                                    variant="caption"
                                    title={getDayJs(`${month}-01`).format('MMMM YYYY')}
                                    sx={{ textAlign: 'center', fontSize: 'clamp(8px, 0.7vw, 10px)' }}
                                  >
                                    {getDayJs(`${month}-01`).format('MMM').slice(0, 1)}
                                  </Typography>
                                ))}
                              </Box>
                            </>
                          )}
                          {sortKey === sortKeys[index] && sortKeys[index] !== 'none' ? (sortDir < 0 ? ' ▾' : ' ▴') : ''}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tours.map((t) => (
                      <React.Fragment key={t.id}>
                        <TableRow
                          className="command-tour-row"
                          hover
                          onClick={() => viewDepartures(t.id)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              viewDepartures(t.id);
                            }
                          }}
                          tabIndex={0}
                          role="button"
                          aria-label={`Open Departure Dashboard: ${t.name}`}
                          sx={{
                            cursor: 'pointer',
                            ...(t.confidenceTier === 'indicative' && {
                              '& td:nth-of-type(n+6):nth-of-type(-n+8)': {
                                fontStyle: 'italic',
                                color: 'text.secondary',
                              },
                            }),
                            '&.Mui-selected > td:first-of-type': { boxShadow: `inset 3px 0 ${tcc.booked}` },
                            '& > .MuiTableCell-root': {
                              verticalAlign: 'top',
                              fontVariantNumeric: 'tabular-nums',
                              py: 1.5,
                              px: 0.5,
                              fontSize: '0.78rem',
                            },
                            '& > td:nth-of-type(n+3):nth-of-type(-n+8)': { lineHeight: '22px', height: 68 },
                            '&:focus-visible': {
                              outline: '2px solid',
                              outlineColor: 'primary.main',
                              outlineOffset: -2,
                            },
                          }}
                        >
                          <TableCell data-label="Tour">
                            {t.name}
                            {t.ltle && ' · LTLE'}
                            <Typography variant="caption" display="block" color="text.secondary">
                              {t.country || '—'} · Departures: {t.deps}
                            </Typography>
                          </TableCell>
                          <TableCell data-label="Region">{t.region}</TableCell>
                          <TableCell
                            data-label="Current Load Factor"
                            align="center"
                            sx={{ bgcolor: groupTint('booked'), fontWeight: 700 }}
                          >
                            <Box
                              component="span"
                              data-testid="tour-metric-value"
                              sx={{ display: 'inline-block', lineHeight: '22px' }}
                            >
                              {pct(t.lf)}
                            </Box>
                          </TableCell>
                          <TableCell data-label="Current TTV" align="center" sx={{ bgcolor: groupTint('booked') }}>
                            <Box
                              component="span"
                              data-testid="tour-metric-value"
                              sx={{ display: 'inline-block', lineHeight: '22px' }}
                            >
                              {moneyShort(t.ttv)}
                            </Box>
                          </TableCell>
                          <TableCell data-label="Current Margin" align="center" sx={{ bgcolor: groupTint('booked') }}>
                            <Box
                              component="span"
                              data-testid="tour-metric-value"
                              sx={{
                                display: 'inline-block',
                                lineHeight: '22px',
                                color: tcc.bookedText,
                              }}
                            >
                              {moneyShort(t.m)}
                            </Box>
                            <Rate rate={t.mp} />
                          </TableCell>
                          <TableCell
                            data-label="Predicted Load Factor"
                            align="center"
                            sx={{
                              bgcolor: groupTint('predicted'),
                              fontWeight: 700,
                              fontStyle: t.confidenceTier === 'indicative' ? 'italic' : 'normal',
                              color: t.confidenceTier === 'indicative' ? 'text.secondary' : undefined,
                            }}
                          >
                            <Box
                              component="span"
                              data-testid="tour-metric-value"
                              sx={{ display: 'inline-block', lineHeight: '22px' }}
                            >
                              {pct(t.plf)}
                            </Box>
                          </TableCell>
                          <TableCell
                            data-label="Predicted TTV"
                            align="center"
                            sx={{
                              bgcolor: groupTint('predicted'),
                              fontStyle: t.confidenceTier === 'indicative' ? 'italic' : 'normal',
                              color: t.confidenceTier === 'indicative' ? 'text.secondary' : undefined,
                            }}
                          >
                            <Box
                              component="span"
                              data-testid="tour-metric-value"
                              sx={{ display: 'inline-block', lineHeight: '22px' }}
                            >
                              {moneyShort(t.pttv)}
                            </Box>
                          </TableCell>
                          <TableCell
                            data-label="Predicted Margin"
                            align="center"
                            sx={{
                              bgcolor: groupTint('predicted'),
                              fontStyle: t.confidenceTier === 'indicative' ? 'italic' : 'normal',
                              color: t.confidenceTier === 'indicative' ? 'text.secondary' : undefined,
                            }}
                          >
                            <Box
                              component="span"
                              data-testid="tour-metric-value"
                              sx={{
                                display: 'inline-block',
                                lineHeight: '22px',
                                color: tcc.predictedText,
                              }}
                            >
                              {moneyShort(t.pmv)}
                            </Box>
                            <Rate rate={t.pm} />
                          </TableCell>
                          <TableCell data-label="Confidence" align="center">
                            <ConfidenceBadge tier={t.confidenceTier} aggregate mix={t.confidenceMix} />
                          </TableCell>
                          <TableCell data-label="Departures" align="center">
                            {t.n}
                          </TableCell>
                          <TableCell data-label="Departures: Hidden" align="center">
                            {t.hidden}
                          </TableCell>
                          <TableCell data-label="Departures: On Track" align="center">
                            {t.onTrack}
                          </TableCell>
                          <TableCell data-label="Next 12 Months (Load Factor)">
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
                                gap: '2px',
                                width: '100%',
                                maxWidth: { xs: 300, lg: 'none' },
                                mx: { xs: 'auto', lg: 0 },
                              }}
                            >
                              {t.hm.map((h) => (
                                <Tooltip
                                  key={h.month}
                                  title={
                                    h.n
                                      ? `${h.month}: Departures: ${h.n}, load factor ${pct(h.lf)}, predicted margin ${pct(h.marginRate)}${h.red ? ' · needs action' : ''}`
                                      : `${h.month}: no departures`
                                  }
                                >
                                  <Box
                                    data-heatmap-empty={!h.n}
                                    sx={{
                                      bgcolor: getMarginColor(h.marginRate),
                                      minWidth: 0,
                                      width: '100%',
                                      height: { xs: 16, lg: 25 },
                                      borderRadius: 0.5,
                                      textAlign: 'center',
                                      position: 'relative',
                                      color:
                                        h.marginRate == null || (h.marginRate > 0 && h.marginRate < 0.1)
                                          ? 'text.primary'
                                          : '#fff',
                                      fontSize: 'clamp(8px, 0.7vw, 10px)',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                    }}
                                  >
                                    {h.red && (
                                      <Box
                                        component="span"
                                        sx={{
                                          position: 'absolute',
                                          top: 2,
                                          right: 2,
                                          width: { xs: 4, lg: 6 },
                                          height: { xs: 4, lg: 6 },
                                          borderRadius: '50%',
                                          bgcolor: '#d03b3b',
                                        }}
                                      />
                                    )}
                                  </Box>
                                </Tooltip>
                              ))}
                            </Box>
                          </TableCell>
                          <TableCell data-label="Departure Actions">
                            <DepartureActionPills
                              rows={t.rows}
                              basis={filters.basis}
                              selected={
                                filters.action !== 'all' ? filters.action : (attentionFlag as CommandFlag | null)
                              }
                            />
                            <Box component="span" sx={{ float: 'right', color: 'text.secondary' }}>
                              ›
                            </Box>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
            <Box
              component="details"
              sx={{
                mt: 4,
                color: 'text.secondary',
                fontSize: 13,
                '& > summary': { cursor: 'pointer' },
                '& > ul': { mt: 1 },
              }}
            >
              <Typography component="summary" variant="subtitle2">
                How It Is Calculated
              </Typography>
              <ul>
                <li>
                  <b>Load Factor</b> = travellers booked ÷ maximum pax, over departures that are on sale or already have
                  bookings (closed dates with no bookings are not counted as capacity).
                </li>
                <li>
                  <b>Current Booked Numbers</b> is what is sold now; <b>Predicted at Handback</b> is where the same
                  departures are expected to finish. They sit side by side everywhere on the page: booked on the left
                  (dark blue), predicted on the right (light blue).
                </li>
                <li>
                  <b>Margin</b> (booked) = what included customers paid (TTV, completed bookings) − supplier cost − ICS
                  at Finance's cost-plus (ICS tours: the ICS cost, from the ICS invoice where there is one, × 1/0.88,
                  less that cost, so 13.6% of it; the same as Finance's ICS profit share table) + money kept from
                  cancellations, as % of TTV. <b>Cost Basis</b>: <i>Sliding Scale</i> (default) prices every booked item
                  at sliding_scale_cost_aud: the base scale read at all travellers on the parent departure (LTLE clone
                  travellers included), VIP / extension / single supplements added as increments; items with no scale
                  use cost_sales_aud ("part est."). On 169 departed ICS-only departures it was within 1.6% of ICS
                  invoices in total (6.3% average error per departure), against 11.3% for cost of sales. <i>Finance</i>{' '}
                  uses cost_sales_aud, including the LTLE booking cost of sales. LTLE and bundle packages count like
                  other travellers: their price, tour cost from the sliding scale at the group size, and their own hotel
                  and add-on cost. Add-ons supplied inside the tour booking are costed from the booking's cost. Packages
                  with no sliding scale use their cost of sales. Bookings still waiting for their hotel order, and
                  bookings with no recorded cost, are left out of margin and noted on the departure. Supplier cost per
                  head is read at the full group size, including travellers whose bookings are not in margin.
                </li>
                <li>
                  <b>Insurance Breakage</b> is added to booked margin and reduces fixed cost in the cost curve, so
                  break-even and predicted margin include it.
                </li>
                <li>
                  <b>Cost</b> = supplier cost, including LTLE and bundle tour, hotel and add-on costs, plus ICS at
                  cost-plus. Margin = TTV − cost, plus any Insurance Breakage, so on those departures margin is higher
                  than TTV − cost. Hover a cost to see the two parts. Cost and margin totals add up the departures whose
                  margin is known (a departure with no price or no recorded cost has none); the card says how many when
                  some are missing.
                </li>
                <li>
                  <b>Predicted</b> (illustrative): where available, the displayed travellers at handback blend a
                  booking-demand model with recent booking pace, and the range shows its lower and upper estimates.
                  Where a model score is unavailable, the page uses recent booking pace. Dates with no bookings use a
                  tour-history estimate where available; the blend is still illustrative and is being tested on new
                  departures. Predicted TTV = what booked travellers paid, plus the travellers still to come at today's
                  price a head (the paid price moved to today's surcharge or discount). Empty dates on sale use their
                  own price today (the cheapest available adult AUD price); dates not on sale have no price for seats
                  still to sell. Predicted cost = predicted travellers × the cost a head at that group size: the
                  suppliers' Base Regular sliding scales (the same rule as the pricing tool: each departure is costed on
                  its coach, the first departure of its clone chain; per supplier, the coach's departure scale, else the
                  tour's scale for the coach's season; ICS at cost-plus), plus the departure's booked cost a head above
                  the scale (tour extras and their ICS), held fixed a head. LTLE hotel and add-on costs are averaged
                  over booked paying travellers at smaller groups and stay fixed above the booked group size; future
                  seats have no package hotel cost. Predicted margin includes ICS at cost-plus and money kept from
                  cancellations, as a share of gross TTV including LTLE. Departures past handback are final: predicted
                  equals booked.
                </li>
                <li>
                  <b>Confidence</b> describes how often similar departure predictions finish within two travellers or
                  10% of capacity, whichever is larger. Long-lead and tour-history estimates are Indicative. Pace
                  fallbacks are capped at Medium. The mix shows each tier's share of predicted travellers.
                </li>

                <li>
                  Seats still to sell are priced at today's price, including any surcharge or discount. Seats already
                  sold keep the price they sold at.
                </li>
                <li>
                  <b>Break-Even</b> = the smallest group at which what its travellers pay (booked seats at what they
                  paid, the rest at today's price) covers its supplier cost, on the same cost a head as predicted cost.
                  It uses the pricing tool's break-even method and supplier scales, plus the departure's hotel and
                  supplement cost a head. One break-even for both cost bases. Break-even load factor = break-even
                  travellers ÷ seats. An unavailable break-even means missing price, cost or supplier curve, or no
                  reachable group size covers cost. Costs can step up again above the first break-even group size.
                  Break-even is shown under predicted travellers in the departures table.
                </li>
                <li>Search Tour filters the whole dashboard. Click a tour row to open its Departure Dashboard.</li>
                <li>
                  <b>By Departure Month / Week</b>: the charts follow every filter, over the selected departure dates.
                  Click a bar to narrow the summary, regions and tours to that month or week.
                </li>
                <li>
                  <b>Margin Bands</b>: We calculate margin at every possible group size, including cost steps that
                  increase cost per person. Cost a head at each size is the suppliers' sliding scale plus the
                  departure's fixed tour cost a head (supplements and their ICS), plus package hotel and add-on costs.
                  The same calibrated traveller distribution supplies the chance for each band and no sale; all seven
                  sum to 100%. Empty departures on sale use their own price today. No sale has no margin percentage and
                  is not a margin action on its own. Missing price or invalid cost is shown as Margin Unavailable.
                  <br />
                  <b>Flags</b>: Needs Action means median predicted margin below 5%, with Medium or High confidence.
                  Watch means median predicted margin below 10%. Low and Indicative confidence risks are capped at
                  Watch. "No Bookings" = on sale with no bookings and handback within 120 days: shown separately because
                  it is an inventory question (close, hold or hand back), not a margin result. Empty dates more than 120
                  days from handback are No Bookings Yet. Departures past handback are shown but not flagged.
                </li>
                <li>
                  <b>Margin %</b> is always a share of sell (TTV). Departure Insights in admin shows (sell − cost) ÷
                  cost, a markup on cost, so its percentages are higher for the same departure.
                </li>
                <li>
                  Partial refunds are not deducted from booked TTV or margin. Over the last six months they were about
                  0.2% of tour TTV.
                </li>
                <li>
                  Known gaps: LTLE flights / cruise fulfilled off-system; zero-booking departures have no cost of their
                  own (predictions use the tour average); load factor counts seats only on dates on sale or booked;
                  "Other / unknown" region = no country on the offer yet.
                </li>
              </ul>
            </Box>
          </>
        )}
      </Container>
    </StalePredictionContext.Provider>
  );
}

export default function ToursCommandCentrePage() {
  return (
    <MUIProvider>
      <PageChatLayout navigationHeight={48}>
        <CommandCentreContent />
      </PageChatLayout>
    </MUIProvider>
  );
}
